import Page from '@app/components/layout/page/page';
import { ReportFallback } from '@app/components/reports/components/report-fallback';
import { SidebarProvider } from '@shared/components/ui/sidebar';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebouncedScrollSnap } from '../hooks/use-debounce-scroll-snap';
import { ReportSection, ReportType } from '../types';
import { ReportPage } from './report-page';
import { ReportSidebar } from './report-sidebar';

interface ReportLayoutProps {
  title: string;
  organizationName: string;
  reportType: ReportType;
  resultStatus: 'loading' | 'error' | 'success';
  resultError?: { type: string; message: string };
  reportUuid: string;

  introComponent: React.ReactNode;
  headerComponent: React.ReactNode;

  data: ReportSection[];
  sections: ReportSection[];

  defaultActive: string;
  downloadMode?: boolean;
}

export function ReportLayout({
  title,
  resultStatus,
  resultError,
  reportType,
  reportUuid,
  defaultActive,
  data,
  sections,
  organizationName,
  downloadMode,
  introComponent: ReportIntro,
  headerComponent: ReportHeader,
}: ReportLayoutProps) {
  const { hash } = useLocation();
  const [active, setActive] = useState<string>(defaultActive);
  const scrollContainerId = 'main-report';
  useDebouncedScrollSnap({
    activePage: active,
    containerId: scrollContainerId,
  });

  return (
    <Page
      name={title}
      status={resultStatus}
      page="guide"
      fallbackComponent={
        <ReportFallback
          reportType={reportType}
          resultError={resultError}
          reportUuid={reportUuid}
        />
      }
    >
      <>
        {!hash && ReportIntro}
        {hash && (
          <SidebarProvider className="flex-col">
            <div
              className="
            overflow-hidden
            [--content-height:calc(100vh-var(--header-height)-var(--title-height))]
            [--content-title-height:calc(100vh-var(--header-height))]
            [--header-height:72px]
            [--page-height:21cm]
            [--page-width:29.7cm] [--title-height:108px]
          "
            >
              <div className="h-[--header-height]">{ReportHeader}</div>
              <div className="flex">
                <div className="h-[--content-title-height]">
                  <ReportSidebar
                    scrollContainerId={scrollContainerId}
                    activeItem={active}
                    data={sections}
                    reportTitle={title}
                    reportType={reportType}
                    organizationName={organizationName}
                  />
                </div>
                <main
                  id={scrollContainerId}
                  className="h-[--content-title-height] flex-1 overflow-y-scroll"
                >
                  {data.map((item) => {
                    if (item.show) {
                      return (
                        <ReportPage
                          key={item.url}
                          containerId={scrollContainerId}
                          item={item}
                          setActive={setActive}
                          downloadMode={downloadMode}
                          reportType={reportType}
                        />
                      );
                    }
                  })}
                </main>
              </div>
            </div>
          </SidebarProvider>
        )}
      </>
    </Page>
  );
}
